import React, { useEffect, useContext } from 'react'
import { useTranslation } from "react-i18next"
import './crudTable.css'
import {
    LoadingContext,
    BufferService,
    MButton,
    MSelect,
    MDrawerTableTextField,
    MInputTextField,
    MFieldset,
    MHeader,
    NavigateContext
} from '../../../M_Components'
import PropTypes from 'prop-types'
import { useMediaQuery } from '@mui/material'
import { useSelector } from 'react-redux'

export function CrudTable({
    metaType,
    setMetaType,
    entity,
    onGetWithFilters,
    operation,
    setIsTable,
    rowEdit,
    setRowEdit,
    valueAdd,
    setValueAdd,
    editMap,
    setEditMap,
    row,
    parentLine,
    field,
    companies,
    isCrudInPopUp
}) {

    const ctx = useContext(LoadingContext)
    const ctx2 = useContext(NavigateContext)

    const M_State = useSelector(state => state.StateKeyValue)

    const bufferService = new BufferService(entity)
    const companyBuffer = new BufferService('Company')
    const { t, i18n } = useTranslation()

    const matches1 = useMediaQuery('(max-width:1580px)')
    const matches2 = useMediaQuery('(max-width:1200px)')
    const matches3 = useMediaQuery('(max-width:820px)')
    const matches4 = useMediaQuery('(max-width:450px)')

    // It comes from the modal of creation
    useEffect(() => {
        if (operation === 'Create') {
            handleOpenCreate()
        }

        if (operation === 'Edit' || operation === 'Copy') {
            handleOpenEdit()
        }
        if (operation === 'View') {
            handleOpenEdit()
        }
        // eslint-disable-next-line
    }, [])

    // When CRUD is used under MTable, metaType is already managed by the parent.
    // In that case I do not want to call options() again here, otherwise I end up
    // with duplicate / redundant "options" calls on each open or language change.
    //
    // I only call options() if:
    //  - there is an entity
    //  - and metaType is empty (meaning this CRUD is being used standalone)
    useEffect(() => {
        if (!entity) return

        const hasMetaFromParent = Array.isArray(metaType) && metaType.length > 0

        if (hasMetaFromParent) {
            // Parent (MTable) already loaded metadata and will also handle language changes.
            // I just keep the current values and let the parent re-render me when it reloads.
            return
        }

        bufferService.options().then(data => {
            if (data?.name === 'AxiosError') {
                ctx.toastMessages('error', data.__message || data.message, '')
                return
            }

            const newMeta = (data || []).map(el => ({
                metaName: el.Name,
                metaType: el.Type,
                metaDisplay: el.NormalizedName,
                metaIsList: el.IsHiddenList,
                metaIsEdit: el.IsHiddenEdit,
                metaMinLen: el.MinLen,
                metaMaxLen: el.MaxLen,
                metaIsRequired: el.IsRequired,
                metaIsForeignKey: el.IsForeignKey,
                metaForeignKeyName: el.ForeignKeyName,
                metaForeignTableName: el.ForeignTableName,
                metaForeignTableToPassId: el.ForeignTableToPassId,
                metaGroupByInfo: el.GroupByInfo
            }))

            // Preserves already entered values and only changes labels/meta
            setEditMap(new Map(editMap))     // Keeps content
            setValueAdd(new Map(valueAdd))   // Keeps content

            // Updates the "skeleton" of labels/groups
            setMetaType?.(newMeta)
        })
        // eslint-disable-next-line
    }, [i18n.language])

    const handleOpenCreate = () => {
        if (valueAdd.size) { return }
        metaType.forEach((elem) => {
            valueAdd.set(elem.metaName, '')
        })
        if (companies?.length === 1) {
            valueAdd.set('UserCompanyId', companies[0].Id)
            valueAdd.set('UserCompany', companies[0].Code)
        }
        setValueAdd(new Map(valueAdd))
    }

    const handleOpenEdit = () => {
        if (editMap.size) { return null }
        if (row) {
            setRowEdit(row)
            setEditMap(new Map(editMap.set('Id', row.Id)))
            metaType.forEach((elem) => {
                editMap.set(elem.metaName, row[elem.metaName])
            })
            if (row?.UserCompanyId) {
                editMap.set('UserCompanyId', row?.UserCompanyId)
                companyBuffer.getId(row?.UserCompanyId).then(companyRes => {
                    if (companyRes.name === 'AxiosError') {
                        ctx.toastMessages('error', companyRes.error, '')
                    }
                    editMap.set('UserCompany', companyRes.Code)
                    setEditMap(new Map(editMap))
                })
            } else if (companies.length !== 0) {
                if (companies.length > 1) {
                    editMap.set('UserCompanyId', '')
                    editMap.set('UserCompany', '')
                } else {
                    editMap.set('UserCompanyId', companies[0].Id)
                    editMap.set('UserCompany', companies[0].Code)
                }
            }

            setEditMap(new Map(editMap))
        } else {
            if (setIsTable) setIsTable(true)
            ctx.toastMessages('error', '', t("SelectedMultiLine"))
        }
    }

    const updateCall = async (Id, data) => {
        const res = await bufferService.update(Id, data).then(data => { return data })
        const resData = await res
        return resData
    }

    const editValues = (name, value) => {
        setEditMap(new Map(editMap.set(name, value)))
    }

    // Transform edit Map to object and submit it to backend
    const submitEdit = () => {
        ctx.setIsLoad(true)
        if (operation === 'Copy') {
            addValue()
            return
        }
        if (parentLine) {
            editMap.set(field, rowEdit[field])
        }

        const tempObjt = Object.fromEntries(editMap)
        updateCall(tempObjt.Id, tempObjt).then(data => {
            if (data.name === 'AxiosError') {
                if (Array.isArray(data?.response?.data?.Errors?.toString())) {
                    ctx.toastMessages('error', data?.response?.data?.Errors?.toString(), 'Erro, ')
                } else {
                    ctx.toastMessages('error', data?.response?.data?.Errors, 'Erro, ')
                }
                ctx.setIsLoad(false)
            } else {
                cleanUp()
                if (setIsTable) setIsTable(true)
                ctx.toastMessages('success', '', t("Success"))
                getWithFilters()
                ctx.setIsLoad(false)
                const id = M_State.find(obj => obj.key === 'selectedTab')?.value
                if (isCrudInPopUp) ctx2.closeTab(id)
            }
        })
    }

    // create
    const post = async (data) => {
        const res = await bufferService.post(data).then(response => { return response })
        const dataRes = await res
        return dataRes
    }

    const addValueState = (key, value) => {
        setValueAdd(new Map(valueAdd.set(key, value)))
    }

    const addValue = () => {
        ctx.setIsLoad(true)
        let obj
        if (operation === 'Copy') {
            if (parentLine) {
                editMap.set(field, rowEdit[field])
            }
            obj = Object.fromEntries(editMap)
        } else {
            if (parentLine) {
                valueAdd.set(field, parentLine[0].Id)
            }
            obj = Object.fromEntries(valueAdd)
        }

        post(obj).then(data => {
            if (data.name === 'AxiosError') {
                if (Array.isArray(data?.response?.data?.Errors?.toString())) {
                    ctx.toastMessages('error', data?.response?.data?.Errors?.toString(), 'Erro, ')
                } else {
                    ctx.toastMessages('error', data?.response?.data?.Errors, 'Erro, ')
                }
                ctx.setIsLoad(false)
            } else {
                ctx.setIsLoad(false)
                cleanUp()
                if (setIsTable) setIsTable(true)
                ctx.toastMessages('success', '', t("AddSuccess"))
                getWithFilters()
                const id = M_State.find(obj => obj.key === 'selectedTab')?.value
                if (isCrudInPopUp) ctx2.closeTab(id)
            }
        })
    }

    const cleanUp = () => {
        setEditMap(new Map(editMap.clear()))
        setValueAdd(new Map(valueAdd.clear()))
        if (setIsTable) setIsTable(true)
    }

    const getWithFilters = () => {
        if (onGetWithFilters) onGetWithFilters()
    }

    const drawerAddValue = (elem, values) => {
        if (values.get(elem.metaForeignTableToPassId)) {
            let tempObj = {}
            tempObj.Id = values.get(elem.metaForeignTableToPassId)
            tempObj[elem.metaForeignKeyName] = values.get(elem.metaName)
            return tempObj
        } else {
            return null
        }
    }

    function addUserCompanyValue(values) {
        if (values.get('UserCompanyId')) {
            return {
                label: values.get('UserCompany'),
                code: values.get('UserCompanyId'),
            }
        }
        return null
    }

    function addUserCompanyColor(elem, value) {
        return !elem.metaIsRequired && !value.get('UserCompany') ? 'var(--color-red)' : ''
    }

    function addInputColor(elem, value) {
        return elem.metaIsRequired && !value.get(elem.metaName) ? 'var(--color-red)' : ''
    }

    function returnFieldsetsData() {
        const newMap = new Map()
        const newArray = []
        const newFields = metaType
            .toSorted((a, b) => { return b?.metaGroupByInfo.localeCompare(a?.metaGroupByInfo) })
            .filter(obj => !obj.metaIsEdit)

        newFields?.forEach(obj => {
            if (!newArray.includes(obj.metaGroupByInfo)) {
                newArray.push(obj.metaGroupByInfo)
                newMap.set(obj.metaGroupByInfo, [...newMap?.get(obj.metaGroupByInfo) || [], obj])
            } else {
                newMap.set(obj.metaGroupByInfo, [...newMap?.get(obj.metaGroupByInfo) || [], obj])
            }
        })
        return [newArray, newMap]
    }

    function fieldsMapping(data, values, valuesFunc) {
        return data?.map(elem => {

            if (!values.keys()) {
                return null
            }

            if (elem.metaName === 'UserCompanyId') {
                if (companies.length === 1) {
                    return (
                        <div key={elem?.metaName} className='textFieldStyle'>
                            <MInputTextField
                                label={'User Company'}
                                type={elem.metaType}
                                required={elem.metaIsRequired}
                                color={addUserCompanyColor(elem, values)}
                                secondColor={addUserCompanyColor(elem, values)}
                                value={values.get('UserCompany') || ''}
                                disabled
                            />
                        </div>
                    )
                }
                return (
                    <div key={elem?.metaName} className='textFieldStyle'>
                        <MSelect
                            size={'300px'}
                            autocomplete
                            label={'Company'}
                            options={companies.map(company => {
                                return { label: company.Code, value: company.Id }
                            })}
                            value={addUserCompanyValue(values)}
                            required={elem.metaIsRequired}
                            color={addUserCompanyColor(elem, values)}
                            secondColor={addUserCompanyColor(elem, values)}
                            onChangeValue={(obj) => {
                                if (obj) {
                                    valuesFunc('UserCompanyId', obj.value)
                                    valuesFunc('UserCompany', obj.label)
                                } else {
                                    valuesFunc('UserCompanyId', '')
                                    valuesFunc('UserCompany', '')
                                }
                            }}
                        />
                    </div>
                )
            } else {
                if (elem.metaIsForeignKey) {
                    return (
                        <div key={elem?.metaName} className='textFieldStyle'>
                            <MDrawerTableTextField
                                size={'300px'}
                                tableLabel={elem.metaForeignTableName}
                                label={elem.metaDisplay}
                                value={drawerAddValue(elem, values)}
                                entity={elem.metaForeignTableName}
                                filterField={elem.metaForeignKeyName}
                                labelFields={[elem.metaForeignKeyName || '']}
                                onSubmitValue={(obj) => {
                                    if (obj) {
                                        valuesFunc(elem.metaForeignTableToPassId, obj.Id)
                                        valuesFunc(elem.metaName, obj[elem.metaForeignKeyName])
                                    } else {
                                        valuesFunc(elem.metaForeignTableToPassId, '')
                                        valuesFunc(elem.metaName, '')
                                    }
                                }}
                                required={elem.metaIsRequired}
                                color={addInputColor(elem, values)}
                                secondColor={addInputColor(elem, values)}
                            />
                        </div>
                    )
                }
                if (() => { return elem.metaType === 'string' || elem.metaType === 'int32' || elem.metaType === 'decimal' }) {
                    return (
                        <div key={elem?.metaName} className='textFieldStyle'>
                            <MInputTextField
                                size={'300px'}
                                maxNumbers={elem.metaMaxLen}
                                label={elem.metaDisplay}
                                type={elem.metaType}
                                required={elem.metaIsRequired}
                                color={addInputColor(elem, values)}
                                secondColor={addInputColor(elem, values)}
                                value={values.get(elem.metaName) || ''}
                                onChange={e => valuesFunc(elem.metaName, e.target.value)}
                            />
                        </div>
                    )
                }
            }
            return null
        })
    }

    function returnViewFields(tempFields) {
        return tempFields.map((meta, index) => {
            if (meta.metaName === 'UserCompanyId') {
                return (
                    <div key={meta?.metaName} className='textFieldStyle'>
                        <MInputTextField
                            size={'300px'}
                            label={'User Company'}
                            value={editMap.get('UserCompany') || ''}
                            disabled
                        />
                    </div>
                )
            }
            if (meta.metaType === 'string' || meta.metaType === 'int32' || meta.metaType === 'decimal') {
                return (
                    <div key={meta?.metaName} className='textFieldStyle'>
                        <MInputTextField
                            size={'300px'}
                            label={meta.metaDisplay}
                            value={editMap.get(meta.metaName) || ''}
                            disabled
                        />
                    </div>
                )
            }
            return null
        })
    }

    function returnFieldsetsElements() {
        const tempData = returnFieldsetsData()
        return tempData[0].map(obj => {
            const tempFields = tempData[1].get(obj)

            return (
                <div
                    key={obj}
                    style={{
                        margin: (isCrudInPopUp && '0px') || (!matches4 && '50px')
                    }}>
                    <MFieldset
                        key={obj}
                        labelInside
                        label={obj}
                        elements={
                            <div
                                style={{
                                    marginBottom: '30px',
                                    display: 'grid',
                                    justifyContent: 'center',
                                    gridTemplateColumns:
                                        (matches3 && 'auto') ||
                                        (isCrudInPopUp && 'auto auto') ||
                                        (matches2 && 'auto auto') ||
                                        (matches1 && 'auto auto auto') ||
                                        'auto auto auto auto',
                                }}>
                                {operation === 'Create' && fieldsMapping(tempFields, valueAdd, addValueState)}
                                {operation === 'View' && returnViewFields(tempFields)}
                                {operation === 'Edit' && fieldsMapping(tempFields, editMap, editValues)}
                                {operation === 'Copy' && fieldsMapping(tempFields, editMap, editValues)}
                            </div>
                        }
                    />
                </div>
            )
        })
    }

    return (
        <div
            className='mainDivCRUD'
            style={{
                position: !isCrudInPopUp && 'sticky',
                minHeight: (isCrudInPopUp && '100%') || '100vh',
                backgroundColor: (isCrudInPopUp && 'white') || 'whitesmoke'
            }}>
            {!isCrudInPopUp && (
                <MHeader
                    element={
                        <>
                            <MButton
                                tooltip={t("Back")}
                                label={t("Back")}
                                icon={
                                    <i
                                        className="pi pi-arrow-left iconTable"
                                        style={{
                                            color: ctx.secondaryColor ? ctx.secondaryColor : 'var(--color-black)'
                                        }}
                                    ></i>
                                }
                                onClick={() => cleanUp()}
                            />
                            {/* Refresh button kept commented out for now
                            <MButton
                                tooltip={t("RefreshTable")}
                                label={operation === 'Create' ? t("Reset") : t("Refresh")}
                                icon={<i className="pi pi-refresh iconTable"></i>}
                                onClick={() => refreshCrud()}
                            /> */}
                            {(operation === 'Create' || operation === 'Copy' || operation === 'Edit') && (
                                <MButton
                                    tooltip={t("Save")}
                                    label={t("Save")}
                                    icon={<i className="pi pi-save iconTable"></i>}
                                    onClick={() => {
                                        if (operation === 'Create' || operation === 'Copy') addValue()
                                        if (operation === 'Edit') submitEdit()
                                    }}
                                />
                            )}
                        </>
                    }
                />
            )}
            <div className='groupTextFields'>
                {returnFieldsetsElements()}
            </div>
            <br /><br /><br /><br /><br />
            {isCrudInPopUp && (
                <div style={{ display: 'flex', justifyContent: 'end' }}>
                    <MButton
                        tooltip={t("Close")}
                        label={t("Close")}
                        backgroundColor={'var(--color-red)'}
                        borderColor={'var(--color-red)'}
                        labelColor={'var(--color-white)'}
                        icon={
                            <i
                                className="pi pi-times iconTable"
                                style={{ color: 'var(--color-white)' }}
                            ></i>
                        }
                        onClick={() => cleanUp()}
                    />
                    {(operation === 'Create' || operation === 'Copy' || operation === 'Edit') && (
                        <MButton
                            tooltip={t("Save")}
                            label={t("Save")}
                            backgroundColor={'var(--color-green)'}
                            borderColor={'var(--color-green)'}
                            labelColor={'var(--color-white)'}
                            icon={
                                <i
                                    className="pi pi-save iconTable"
                                    style={{ color: 'var(--color-white)' }}
                                ></i>
                            }
                            onClick={() => {
                                if (operation === 'Create' || operation === 'Copy') addValue()
                                if (operation === 'Edit') submitEdit()
                            }}
                        />
                    )}
                </div>
            )}
        </div>
    )
}

CrudTable.propTypes = {
    metaType: PropTypes.array,
    setMetaType: PropTypes.func,
    entity: PropTypes.string,
    onGetWithFilters: PropTypes.func,
    operation: PropTypes.string,
    setIsTable: PropTypes.func,
    rowEdit: PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.object,
    ]),
    setRowEdit: PropTypes.func,
    valueAdd: PropTypes.instanceOf(Map),
    setValueAdd: PropTypes.func,
    editMap: PropTypes.instanceOf(Map),
    setEditMap: PropTypes.func,
    row: PropTypes.object,
    parentLine: PropTypes.array,
    field: PropTypes.string,
    companies: PropTypes.array,
    isCrudInPopUp: PropTypes.bool
}
